import { useEffect, useId, useRef, useState } from "react";

import { type Theme, useTheme } from "../lib/theme.tsx";

// Mermaid é pesado (~1 MB) e a maioria das páginas não tem diagrama, então ele
// entra por import dinâmico: só é baixado quando um <Mermaid> monta de fato.
let mermaidPromise: Promise<typeof import("mermaid").default> | null = null;

function loadMermaid() {
  if (!mermaidPromise) mermaidPromise = import("mermaid").then((m) => m.default);
  return mermaidPromise;
}

// As cores do diagrama são fixadas em `initialize()`, não lidas do CSS — então
// trocar de tema exige reconfigurar e renderizar de novo. Sem isso o diagrama
// fica com texto escuro sobre fundo escuro.
const THEME_VARS: Record<Theme, Record<string, string>> = {
  light: {
    primaryColor: "#ffffff",
    primaryBorderColor: "#1A1F30",
    primaryTextColor: "#1A1F30",
    lineColor: "#9CA3AF",
  },
  dark: {
    primaryColor: "#12161F",
    primaryBorderColor: "#C3C8D4",
    primaryTextColor: "#E9EBF0",
    lineColor: "#6F7889",
  },
};

// `initialize` é global no mermaid. Como toda instância re-renderiza quando o
// tema muda, todas passam por aqui com o mesmo valor — não há risco de dois
// diagramas ficarem em temas diferentes.
function configure(mermaid: typeof import("mermaid").default, theme: Theme) {
  mermaid.initialize({
    startOnLoad: false,
    theme: "base",
    themeVariables: { fontFamily: "Geist, sans-serif", ...THEME_VARS[theme] },
  });
}

export default function Mermaid({ chart, className = "flow-diagram" }: { chart: string; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  // O diagrama depende de um import dinâmico + render assíncrono. Sem o fade,
  // ele aparece de estalo bem depois do resto da página.
  const [ready, setReady] = useState(false);
  const { theme } = useTheme();

  // useId gera "«r1»" — mermaid usa o id em seletores CSS, e os guillemets
  // quebram o seletor. Só as partes alfanuméricas sobrevivem.
  const rawId = useId();
  const chartId = "mmd-" + rawId.replace(/[^a-zA-Z0-9]/g, "");

  useEffect(() => {
    let cancelled = false;

    const render = async () => {
      try {
        const mermaid = await loadMermaid();
        if (cancelled) return;
        configure(mermaid, theme);
        // Fontes ainda carregando fazem o mermaid medir os textos errado e o
        // diagrama sai com os nós cortados.
        if (document.fonts?.ready) await document.fonts.ready;
        if (cancelled) return;
        // Id único por tema: o mermaid injeta <style> por id de render e
        // reaproveitaria as cores antigas se o id repetisse.
        const { svg } = await mermaid.render(`${chartId}-${theme}-svg`, chart);
        if (!cancelled && ref.current) {
          ref.current.innerHTML = svg;
          setReady(true);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };

    render();
    return () => {
      cancelled = true;
    };
  }, [chart, chartId, theme]);

  if (error) {
    return (
      <pre className="doc-code doc-code-error">
        <code>{error}</code>
      </pre>
    );
  }
  return <div className={`${className} mermaid-fade ${ready ? "ready" : ""}`} ref={ref} />;
}
